import {
  IonCheckbox,
  IonLabel,
  IonList,
  IonListHeader,
  isPlatform,
} from '@ionic/react';
import { ReactElement, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '../../api/authorization.provider';
import {
  CreateEventSubscriptionInput,
  EventChannel,
  EventChannelType,
  EventDefinition,
  ListEventDefinitionsDocument,
  ListEventSubscriptionsDocument,
  ListEventSubscriptionsQueryVariables,
  UpdateEventDefinitionInput,
  useCreateEventSubscriptionMutation,
  useListEventChannelsQuery,
  useListEventSubscriptionsQuery,
  useRemoveEventSubscriptionMutation,
} from '../../api/graphql/generated';
import { useRegisterPushNotification } from '../../api/hooks/push-notification.hooks';
import { IonItem } from '../ionic/IonItem';
import { SaveStatusToast } from '../toast/toast.gql';
import { EventDefinitionForm } from './event-definition-form';

interface EventDetailProps {
  eventDefinition: EventDefinition;
}

const EventDefinitionDetail = (props: EventDetailProps): ReactElement => {
  const { eventDefinition } = props;
  const { i18n } = useTranslation();

  const removeSubscriptionError = useRef<boolean>(false);

  const listEventSubscriptionVariables: ListEventSubscriptionsQueryVariables = {
    input: {
      eventDefinitionId: eventDefinition.id,
    },
  };

  const canUpdateEventDefinition = useHasPermission(
    'update',
    'event_definition',
    eventDefinition
  );

  const { data: eventSubscriptionList } = useListEventSubscriptionsQuery({
    variables: listEventSubscriptionVariables,
    skip: eventDefinition === undefined,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  const { data: eventChannelList } = useListEventChannelsQuery({
    variables: {
      input: {
        object: eventDefinition.object,
        active: true,
      },
    },
    fetchPolicy: 'no-cache',
  });

  const [registerPushNotifications] = useRegisterPushNotification();

  const subscriptionList =
    eventSubscriptionList?.listEventSubscriptions.items ?? [];
  const channelList = eventChannelList?.listEventChannels.items ?? [];
  const [
    createEventSubscription,
    { error: createError, loading: createLoading },
  ] = useCreateEventSubscriptionMutation();
  const [
    removeEventSubscription,
    { error: removeError, loading: removeLoading },
  ] = useRemoveEventSubscriptionMutation();

  const toggleSubscription = async (
    channel: EventChannel,
    shouldSubscribe: boolean,
    subscriptionIdx: number
  ): Promise<boolean> => {
    removeSubscriptionError.current = false;
    if (shouldSubscribe) {
      const registered = await registerPushNotifications({
        hideDeniedPrompt: false,
        hideRequestPrompt: false,
        skipRegisterIfAlreadyGranted: false,
      });

      if (registered) {
        const subscribed = await subscribe(channel);
        if (subscribed === undefined) {
          return false;
        }

        return subscribed.errors ? subscribed.errors.length > 0 : true;
      } else {
        return false;
      }
    } else {
      const unsubscribed = await unsubscribe(subscriptionIdx);
      if (unsubscribed === undefined) {
        return false;
      }

      return unsubscribed.errors ? unsubscribed.errors.length > 0 : true;
    }
  };

  const subscribe = async (channel: EventChannel | undefined) => {
    if (!channel) {
      return;
    }

    const input: CreateEventSubscriptionInput = {
      eventChannelId: channel.id,
      eventDefinitionId: eventDefinition.id,
    };

    const result = await createEventSubscription({
      variables: {
        input,
      },
      refetchQueries: [
        {
          query: ListEventSubscriptionsDocument,
          variables: listEventSubscriptionVariables,
        },
        ListEventDefinitionsDocument,
      ],
    });

    return result;
  };

  const unsubscribe = async (subscriptionIdx: number) => {
    if (subscriptionIdx === -1) {
      return;
    }
    const eventSubscriptionIdx = subscriptionList[subscriptionIdx];
    const result = await removeEventSubscription({
      variables: {
        input: {
          id: eventSubscriptionIdx.id,
        },
      },
      refetchQueries: [
        {
          query: ListEventSubscriptionsDocument,
          variables: listEventSubscriptionVariables,
        },
        ListEventDefinitionsDocument,
      ],
    });
    removeSubscriptionError.current =
      result.data?.removeEventSubscription === false ? true : false;
    return result;
  };

  const channels = channelList.map((channel) => {
    const subscriptionIdx = subscriptionList.findIndex(
      (subscription) => subscription.eventChannel?.id === channel.id
    );
    return (
      <EventChannelToggle
        key={channel.id}
        channel={channel}
        toggleSubscription={toggleSubscription}
        checked={subscriptionIdx > -1}
        subscriptionIdx={subscriptionIdx}
      />
    );
  });

  const currentLabel = eventDefinition.label || eventDefinition.name;
  const form: UpdateEventDefinitionInput = {
    id: eventDefinition.id,
    label: currentLabel,
  };

  const hasUnsubError = removeSubscriptionError.current ? true : undefined;

  return (
    <>
      {canUpdateEventDefinition ? (
        <EventDefinitionForm form={form}></EventDefinitionForm>
      ) : (
        <IonListHeader>
          <IonLabel>{currentLabel}</IonLabel>
        </IonListHeader>
      )}
      <IonListHeader>
        <IonLabel>{i18n.t('common:event_channels')}</IonLabel>
      </IonListHeader>
      <IonList lines="full">{channels}</IonList>
      <SaveStatusToast
        loading={createLoading || removeLoading}
        error={createError || removeError || hasUnsubError}
      />
    </>
  );
};

interface EventChannelToggleProps {
  channel: EventChannel;
  toggleSubscription: (
    channel: EventChannel,
    shouldSubscribe: boolean,
    subscription: number
  ) => Promise<boolean>;
  checked: boolean;
  subscriptionIdx: number;
}

const EventChannelToggle = (props: EventChannelToggleProps): ReactElement => {
  const { checked, channel, toggleSubscription } = props;
  const { i18n } = useTranslation();
  const [calling, setCalling] = useState<boolean>(false);

  const toggleCheck = async (shouldSubscribe: boolean) => {
    if (calling) {
      return;
    }
    setCalling(true);
    await toggleSubscription(channel, shouldSubscribe, props.subscriptionIdx);
    setCalling(false);
  };

  const showWebSubtitle =
    channel.type === EventChannelType.USER && !isPlatform('capacitor');
  return (
    <IonItem>
      <IonCheckbox
        color="success"
        checked={checked}
        disabled={calling}
        onClick={() => {
          toggleCheck(!checked);
        }}
      >
        <IonLabel>
          <h2>{channel.label}</h2>
          {showWebSubtitle && (
            <p>{i18n.t('common:event_mobile_needed_for_notifications')}</p>
          )}
        </IonLabel>
      </IonCheckbox>
    </IonItem>
  );
};

export { EventDefinitionDetail };
